// Locale-aware formatting helpers.
//
// Money keeps Belgian dot-grouping in both languages on purpose: the price
// strings authored in data/services.js ("€1.800 – €2.500") use that convention,
// so a locale-switched separator would clash with them on the same screen.

const INTL_LOCALE = { en: 'en-BE', nl: 'nl-BE' };

export const intlLocale = (lang) => INTL_LOCALE[lang] || INTL_LOCALE.en;

export const groupThousands = (n) => {
  if (typeof n !== 'number' || Number.isNaN(n)) return '0';
  return n.toLocaleString('nl-BE').replace(/,/g, '.');
};

export const formatEuro = (n) => `\u20ac${groupThousands(n)}`;

// English month names as authored in data/projects.js + data/insights.js.
const MONTHS = [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december',
];

// Parses the "June 2025" / "12 June 2025" shapes used across the data files.
const parseAuthoredDate = (value) => {
  if (typeof value !== 'string') return null;
  const match = value.trim().match(/^(?:(\d{1,2})\s+)?([A-Za-z]+)\s+(\d{4})$/);
  if (!match) return null;
  const monthIndex = MONTHS.indexOf(match[2].toLowerCase());
  if (monthIndex === -1) return null;
  return { day: match[1] ? Number(match[1]) : null, monthIndex, year: Number(match[3]) };
};

// "June 2025" -> "juni 2025" on /nl, unchanged on /en. Anything we can't parse
// is passed through untouched so hand-written dates never break.
export const formatAuthoredDate = (value, lang) => {
  const parsed = parseAuthoredDate(value);
  if (!parsed) return value;
  const date = new Date(parsed.year, parsed.monthIndex, parsed.day || 1);
  const options = parsed.day
    ? { day: 'numeric', month: 'long', year: 'numeric' }
    : { month: 'long', year: 'numeric' };
  try {
    return date.toLocaleDateString(intlLocale(lang), options);
  } catch (_) {
    return value;
  }
};

// "~8 min. read" -> "~8 min. leestijd". `suffix` comes from messages so the
// wording stays editable per locale.
export const formatReadTime = (value, suffix) => {
  if (typeof value !== 'string' || !suffix) return value;
  const minutes = value.match(/~?\s*(\d+)/);
  if (!minutes) return value;
  const prefix = value.trim().startsWith('~') ? '~' : '';
  return `${prefix}${minutes[1]} ${suffix}`;
};

// Stable 24-hour clock. en-GB rather than en-US because en-US with
// hour12: false renders midnight as "24:00:00" in several engines.
export const formatClock = (date) =>
  date.toLocaleTimeString('en-GB', { hour12: false });

// Fills {name}/{n}-style placeholders in a translated string.
export const interpolate = (template, values = {}) => {
  if (typeof template !== 'string') return template;
  return template.replace(/\{(\w+)\}/g, (match, key) =>
    Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : match
  );
};
